// ═══════════════════════════════════════════════════════════════════════════════
// Login Page (public)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getRedirectAfterAuth } from '../../lib/authRedirect';
import { tryCompletePairInviteAfterAuth } from '../../lib/completePendingPairInvite';
import { ApiClientError } from '../../lib/api';
import { hasGoogleClientId, startGoogleRedirectAuth } from '../../lib/googleIdentity';
import { hasPendingPairInvite, readPendingPairInvite } from '../../lib/pairInviteSession';
import { AuthLayout } from './AuthLayout';
import { PasswordField } from '../../components/auth/PasswordField';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AppButton } from '../../components/ui/AppButton';
import { getClientChannel } from '../../lib/clientChannel';

type LocationState = { from?: string };

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { login, refreshUser, isAuthenticated, user, isLoading: authBooting } = useAuth();

  const showRememberMe = getClientChannel() === 'web';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [googleSwitchOpen, setGoogleSwitchOpen] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const from = (location.state as LocationState | null)?.from;

  const pendingInvite = readPendingPairInvite();

  const handlePostLoginNavigate = useCallback(
    async (nextUser: NonNullable<typeof user>) => {
      if (hasPendingPairInvite()) {
        const result = await tryCompletePairInviteAfterAuth(nextUser, refreshUser, navigate);
        if (result === 'joined') {
          return;
        }
      }
      navigate(getRedirectAfterAuth(nextUser, from), { replace: true });
    },
    [from, navigate, refreshUser]
  );

  useEffect(() => {
    if (authBooting || !isAuthenticated || !user) return;
    void handlePostLoginNavigate(user);
  }, [authBooting, isAuthenticated, user, handlePostLoginNavigate]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setIsSubmitting(true);

      try {
        const nextUser = await login(
          email.trim(),
          password,
          showRememberMe ? rememberMe : true
        );
        await handlePostLoginNavigate(nextUser);
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'GOOGLE_LOGIN_REQUIRED') {
          setGoogleSwitchOpen(true);
          return;
        }
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, rememberMe, showRememberMe, login, handlePostLoginNavigate, t]
  );

  const handleToggleRemember = useCallback(() => {
    setRememberMe((v) => !v);
  }, []);

  const handleCloseGoogleSwitch = useCallback(() => {
    if (googleBusy) {
      return;
    }
    setGoogleSwitchOpen(false);
  }, [googleBusy]);

  const handleConfirmGoogleSwitch = useCallback(() => {
    setGoogleBusy(true);
    setError('');
    try {
      const invite = readPendingPairInvite();
      startGoogleRedirectAuth({
        intent: 'login',
        pairInviteMode: Boolean(invite),
        pairingCode: invite?.code,
        role: 'helper',
        fromPath: invite ? `/pair-code/${invite.code}` : from,
      });
    } catch (err) {
      setGoogleBusy(false);
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    }
  }, [from, t]);

  if (authBooting) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-gray-50"
        role="status"
        aria-label={t('common.loading')}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <AuthLayout
      backTo="/"
      footer={
        <p>
          <Link className="font-medium text-primary-600 underline" to="/register">
            {t('auth.linkRegister')}
          </Link>
        </p>
      }
    >
      <h1 className="text-2xl font-bold text-gray-900" id="login-heading">
        {t('login.title')}
      </h1>
      <p className="mt-1 text-sm text-gray-500">{t('login.subtitle')}</p>

      {pendingInvite ? (
        <p className="mt-4 rounded-lg bg-primary-50 px-3 py-2 text-sm text-primary-900 dark:bg-primary-950/40 dark:text-primary-100">
          {t('pairing.invite.loginBanner', { nickname: pendingInvite.employerNickname })}
        </p>
      ) : null}

      <form
        className="mt-6 space-y-4"
        onSubmit={handleSubmit}
        aria-labelledby="login-heading"
        noValidate
      >
        {error ? (
          <div
            id="login-error"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-gray-700">
            {t('auth.emailLabel')}
          </label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'login-error' : undefined}
          />
        </div>

        <PasswordField
          id="login-password"
          name="password"
          label={t('auth.passwordLabel')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          disabled={isSubmitting}
          showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'login-error' : undefined}
        />

        <div className="flex items-center justify-between gap-3">
          {showRememberMe ? (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={handleToggleRemember}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                aria-label={t('auth.rememberMe')}
              />
              <span>{t('auth.rememberMe')}</span>
            </label>
          ) : (
            <span className="flex-1" aria-hidden="true" />
          )}
          <Link
            className="text-sm font-medium text-primary-600 underline"
            to="/forgot-password"
          >
            {t('auth.forgotPasswordLink')}
          </Link>
        </div>

        <AppButton type="submit" variant="primary" fullWidth disabled={isSubmitting} className="shadow-sm">
          {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
        </AppButton>
      </form>

      <ConfirmDialog
        open={googleSwitchOpen}
        title={t('auth.googleLoginRequiredTitle')}
        description={t('auth.googleLoginRequiredDescription')}
        confirmLabel={
          googleBusy || !hasGoogleClientId()
            ? t('common.loading')
            : t('auth.googleLoginRequiredConfirm')
        }
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmGoogleSwitch}
        onCancel={handleCloseGoogleSwitch}
      />
    </AuthLayout>
  );
};
